import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Button, Modal } from 'react-bootstrap';
import { useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { RootState } from '../../../store/store';
import {
  getGetLoanApplicationQueryKey,
  useGetLoanApplicationByTask,
  useSubmitCreditApproval
} from '../../../services/loanService';
import { ApprovalConditionType } from '../../../services/schemas/loanServiceSchemas/approvalConditionType';
import { CommitteeDecision } from '../../../services/schemas/loanServiceSchemas/committeeDecision';
import { NewApprovalCondition } from '../../../services/schemas/loanServiceSchemas/newApprovalCondition';
import IndividualAffordabilityVerification from './IndividualAffordabilityVerification';
import VerificationSummary from '../../common/VerificationSummary';
import CollateralSummary from '../../common/CollateralSummary';
import CreditAssessmentSummary from '../../common/CreditAssessmentSummary';
import { formatCurrency, formatDate } from '../../../utils/formatters';
import { DECLINE_REASON_LABELS } from '../../../constants/declineReasons';
import type { DeclineReason } from '../../../services/schemas/loanServiceSchemas/declineReason';

/**
 * The Credit Committee's step: everything the earlier stages concluded, then the decision.
 *
 * Approving records the approved amount, the rationale and any conditions; each condition stays
 * Outstanding on the application until it is fulfilled or waived, and one marked "before
 * disbursement" holds back the payout until then.
 */

interface ConditionDraft {
  key: number;
  description: string;
  conditionType: ApprovalConditionType | '';
  dueDate: string;
}

let nextKey = 1;
const blankCondition = (): ConditionDraft => ({ key: nextKey++, description: '', conditionType: '', dueDate: '' });

const CreditApprovalDetails: React.FC = () => {
  const { taskId } = useParams<{ taskId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = useSelector((state: RootState) => state.auth.user);

  const { data: application, isLoading, error, refetch } = useGetLoanApplicationByTask(taskId || '', {
    query: { enabled: !!taskId }
  });

  const [decision, setDecision] = useState<CommitteeDecision | ''>('');
  const [approvedAmount, setApprovedAmount] = useState('');
  const [rationale, setRationale] = useState('');
  // Reportable reason category, required to reject (the rationale gives the reason in words)
  const [declineReason, setDeclineReason] = useState('');
  const [conditions, setConditions] = useState<ConditionDraft[]>([]);
  const [showConfirm, setShowConfirm] = useState(false);
  const [submitted, setSubmitted] = useState<CommitteeDecision | null>(null);

  const isIndividual = application?.loanType === 'PERSONAL';
  const assessedAmount = application?.loanDetails?.loanAmount;

  // The approved amount starts at what credit assessed; the committee may approve less.
  useEffect(() => {
    if (assessedAmount != null && (approvedAmount === '' || isIndividual)) {
      setApprovedAmount(String(assessedAmount));
    }
  }, [assessedAmount, approvedAmount, isIndividual]);

  const submitDecision = useSubmitCreditApproval();

  const amountValue = Number(approvedAmount);
  const amountError = decision === 'APPROVE'
    ? (!approvedAmount || !(amountValue > 0)
      ? 'Enter the approved amount'
      : assessedAmount != null && amountValue > assessedAmount
        ? `Cannot exceed the assessed amount of ${formatCurrency(assessedAmount)}`
        : '')
    : '';
  const incompleteCondition = decision === 'APPROVE'
    && conditions.some(c => !c.description.trim() || !c.conditionType);
  const canSubmit = !!decision && !!rationale.trim() && !amountError && !incompleteCondition
    && (decision !== 'REJECT' || !!declineReason);

  const updateCondition = (key: number, changes: Partial<ConditionDraft>) =>
    setConditions(current => current.map(c => (c.key === key ? { ...c, ...changes } : c)));

  const handleSubmit = async () => {
    if (!application?.id || !decision) return;
    const payload: NewApprovalCondition[] = decision === 'APPROVE'
      ? conditions.map(c => ({
          description: c.description.trim(),
          conditionType: c.conditionType as ApprovalConditionType,
          dueDate: c.dueDate || undefined
        }))
      : [];
    try {
      await submitDecision.mutateAsync({
        applicationId: application.id,
        data: {
          decision,
          approvedAmount: decision === 'APPROVE' ? amountValue : undefined,
          rationale: rationale.trim(),
          conditions: payload,
          recordedBy: user?.name || user?.username,
          declineReason: decision === 'REJECT' ? (declineReason as DeclineReason) : undefined
        }
      });
      await queryClient.invalidateQueries({ queryKey: getGetLoanApplicationQueryKey(application.id) });
      setShowConfirm(false);
      setSubmitted(decision);
      toast.success(decision === 'APPROVE' ? 'Application approved' : 'Application rejected');
    } catch (err: any) {
      setShowConfirm(false);
      toast.error(err?.response?.data?.message || err?.message || 'Could not record the decision');
    }
  };

  if (isLoading) {
    return (
      <div className="container-fluid p-4 text-center">
        <div className="spinner-border text-primary" role="status"></div>
        <p className="mt-3 text-muted">Loading the application…</p>
      </div>
    );
  }

  if (error || !application) {
    return (
      <div className="container-fluid p-4">
        <div className="alert alert-danger">
          Could not load this application. It may already have been decided.
          <div className="mt-2">
            <Link to="/work-items" className="btn btn-sm btn-outline-danger">Back to work items</Link>
          </div>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="container-fluid p-4">
        <div className="card">
          <div className="card-body text-center py-5">
            <i className={`fa ${submitted === 'APPROVE' ? 'fa-check-circle text-success' : 'fa-times-circle text-danger'}`}
               style={{ fontSize: '4rem' }}></i>
            <h2 className="mt-3 mb-2">{submitted === 'APPROVE' ? 'Application approved' : 'Application rejected'}</h2>
            <p className="text-muted mb-4">
              {submitted === 'APPROVE'
                ? conditions.length > 0
                  ? `The decision and ${conditions.length} ${conditions.length === 1 ? 'condition are' : 'conditions are'} recorded on the application. The loan agreement is next.`
                  : 'The decision is recorded on the application. The loan agreement is next.'
                : 'The decision is recorded on the application and the customer will be notified.'}
            </p>
            <div className="d-flex justify-content-center gap-2">
              <Button variant="outline-primary" onClick={() => navigate(`/application/${application.id}`)}>
                View application
              </Button>
              <Button variant="primary" onClick={() => navigate('/work-items')}>Return to work items</Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const applicantName = application.company?.companyName
    || [application.individual?.firstName, application.individual?.lastName].filter(Boolean).join(' ')
    || application.customerNumber;
  const details = application.loanDetails;

  return (
    <>
      <div className="page-toolbar px-xl-4 px-sm-2 px-0 py-3">
        <div className="container-fluid">
          <ol className="breadcrumb bg-transparent mb-0">
            <li className="breadcrumb-item"><Link className="text-secondary" to="/">Home</Link></li>
            <li className="breadcrumb-item"><Link className="text-secondary" to="/work-items">Work Items</Link></li>
            <li className="breadcrumb-item active" aria-current="page">Credit Committee approval</li>
          </ol>
        </div>
      </div>

      <div className="page-body px-xl-4 px-sm-2 px-0 py-lg-2 py-1">
        <div className="container-fluid">
          <div className="card mb-3">
            <div className="card-header d-flex justify-content-between align-items-center flex-wrap gap-2">
              <h6 className="card-title mb-0">
                Credit Committee approval · {application.applicationNumber}
                <span className="ms-2 text-muted">| {applicantName}</span>
              </h6>
              <Link
                to={`/application/${application.id}`}
                state={{ taskId }}
                className="btn btn-sm btn-outline-primary"
              >
                <i className="bi bi-eye me-2"></i>View full application
              </Link>
            </div>
            <div className="card-body">
              <div className="row g-3">
                <div className="col-6 col-md-3">
                  <div className="small text-muted">Assessed amount</div>
                  <div className="fw-semibold">{assessedAmount != null ? formatCurrency(assessedAmount) : '-'}</div>
                </div>
                <div className="col-6 col-md-3">
                  <div className="small text-muted">Interest rate</div>
                  <div className="fw-semibold">{details?.interestRate != null ? `${details.interestRate}%` : '-'}</div>
                </div>
                <div className="col-6 col-md-3">
                  <div className="small text-muted">Instalments</div>
                  <div className="fw-semibold">{details?.numberOfInstallments ?? '-'}</div>
                </div>
                <div className="col-6 col-md-3">
                  <div className="small text-muted">Due date</div>
                  <div className="fw-semibold">{details?.dueDate ? formatDate(details.dueDate) : '-'}</div>
                </div>
              </div>
            </div>
          </div>

          {isIndividual && <IndividualAffordabilityVerification application={application} onRequoted={() => { void refetch(); }} />}

          {/* What the earlier stages concluded, in the order they ran. */}
          <VerificationSummary applicationId={application.id} />
          <CollateralSummary applicationId={application.id} />
          <CreditAssessmentSummary applicationId={application.id} />

          <div className="card mb-4">
            <div className="card-header py-2">
              <h6 className="card-title mb-0"><i className="fa fa-gavel me-2"></i>Decision</h6>
            </div>
            <div className="card-body">
              <div className="d-flex gap-2 mb-3 flex-wrap">
                <Button
                  variant={decision === 'APPROVE' ? 'success' : 'outline-success'}
                  onClick={() => setDecision('APPROVE')}
                >
                  <i className="fa fa-check me-2"></i>Approve
                </Button>
                <Button
                  variant={decision === 'REJECT' ? 'danger' : 'outline-danger'}
                  onClick={() => setDecision('REJECT')}
                >
                  <i className="fa fa-times me-2"></i>Reject
                </Button>
              </div>

              {decision === 'APPROVE' && (
                <div className="mb-3" style={{ maxWidth: 320 }}>
                  <label htmlFor="approved-amount" className="form-label">
                    Approved amount <span className="text-danger">*</span>
                  </label>
                  <input
                    id="approved-amount"
                    readOnly={isIndividual}
                    type="number"
                    min={0}
                    step="0.01"
                    className={`form-control ${amountError ? 'is-invalid' : ''}`}
                    value={approvedAmount}
                    onChange={e => setApprovedAmount(e.target.value)}
                  />
                  {amountError
                    ? <div className="invalid-feedback">{amountError}</div>
                    : <div className="form-text">{isIndividual ? 'Revise the retained quotation and reassess before approving a different amount.' : 'A lower amount re-prices the loan.'}</div>}
                </div>
              )}

              {decision === 'REJECT' && (
                <div className="mb-3">
                  <label htmlFor="decline-reason" className="form-label">
                    Reason for rejecting <span className="text-danger">*</span>
                  </label>
                  <select
                    id="decline-reason"
                    className="form-select"
                    value={declineReason}
                    onChange={e => setDeclineReason(e.target.value)}
                  >
                    <option value="">Select a reason</option>
                    {Object.entries(DECLINE_REASON_LABELS).map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                  <div className="form-text">Used to report on why business is lost.</div>
                </div>
              )}

              {decision && (
                <div className="mb-3">
                  <label htmlFor="approval-rationale" className="form-label">
                    Rationale <span className="text-danger">*</span>
                  </label>
                  <textarea
                    id="approval-rationale"
                    className="form-control"
                    rows={4}
                    value={rationale}
                    onChange={e => setRationale(e.target.value)}
                    placeholder={decision === 'APPROVE'
                      ? 'Why the committee is approving, and on what basis'
                      : 'Why the committee is rejecting the application'}
                  />
                </div>
              )}

              {decision === 'APPROVE' && (
                <div className="mb-2">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <label className="form-label mb-0">Conditions</label>
                    <Button variant="outline-primary" size="sm" onClick={() => setConditions(c => [...c, blankCondition()])}>
                      <i className="fa fa-plus me-1"></i>Add condition
                    </Button>
                  </div>
                  {conditions.length === 0 && (
                    <p className="small text-muted mb-0">No conditions. Add one for anything that must still be provided or done.</p>
                  )}
                  {conditions.map((condition, index) => (
                    <div key={condition.key} className="border rounded p-3 mb-2">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <span className="small fw-semibold">Condition {index + 1}</span>
                        <Button
                          variant="link"
                          size="sm"
                          className="text-danger p-0"
                          onClick={() => setConditions(c => c.filter(x => x.key !== condition.key))}
                        >
                          Remove
                        </Button>
                      </div>
                      <textarea
                        className="form-control mb-2"
                        rows={2}
                        placeholder="e.g. Signed suretyship from each director"
                        value={condition.description}
                        onChange={e => updateCondition(condition.key, { description: e.target.value })}
                        aria-label={`Condition ${index + 1} description`}
                      />
                      <div className="row g-2">
                        <div className="col-12 col-md-6">
                          <select
                            className="form-select"
                            value={condition.conditionType}
                            onChange={e => updateCondition(condition.key, { conditionType: e.target.value as ApprovalConditionType })}
                            aria-label={`Condition ${index + 1} timing`}
                          >
                            <option value="">When must it be met?</option>
                            <option value="BEFORE_DISBURSEMENT">Before disbursement (blocks payout)</option>
                            <option value="AFTER_DISBURSEMENT">After disbursement (tracked only)</option>
                          </select>
                        </div>
                        <div className="col-12 col-md-6">
                          <input
                            type="date"
                            className="form-control"
                            value={condition.dueDate}
                            onChange={e => updateCondition(condition.key, { dueDate: e.target.value })}
                            aria-label={`Condition ${index + 1} due date (optional)`}
                            title="Due date (optional)"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                  {incompleteCondition && (
                    <div className="small text-danger">Every condition needs a description and when it must be met.</div>
                  )}
                </div>
              )}

              <div className="d-flex justify-content-end mt-3">
                <Button
                  variant={decision === 'REJECT' ? 'danger' : 'primary'}
                  disabled={!canSubmit || submitDecision.isPending}
                  onClick={() => setShowConfirm(true)}
                >
                  Record decision
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Modal show={showConfirm} onHide={() => !submitDecision.isPending && setShowConfirm(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="fs-6">
            {decision === 'APPROVE' ? 'Approve this application?' : 'Reject this application?'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="small">
          {decision === 'APPROVE' ? (
            <>
              <p className="mb-2">Approved amount: <strong>{formatCurrency(amountValue)}</strong></p>
              {conditions.length > 0 ? (
                <>
                  <p className="mb-1">With {conditions.length} {conditions.length === 1 ? 'condition' : 'conditions'}:</p>
                  <ul className="mb-0">
                    {conditions.map(c => (
                      <li key={c.key}>
                        {c.description.trim()}
                        <span className="text-muted">
                          {' '}({c.conditionType === 'BEFORE_DISBURSEMENT' ? 'before disbursement' : 'after disbursement'})
                        </span>
                      </li>
                    ))}
                  </ul>
                </>
              ) : <p className="mb-0">No conditions.</p>}
            </>
          ) : (
            <p className="mb-0">The application ends here and the customer is notified.</p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" size="sm" onClick={() => setShowConfirm(false)} disabled={submitDecision.isPending}>
            Go back
          </Button>
          <Button
            variant={decision === 'REJECT' ? 'danger' : 'success'}
            size="sm"
            onClick={handleSubmit}
            disabled={submitDecision.isPending}
          >
            {submitDecision.isPending && <span className="spinner-border spinner-border-sm me-2" role="status"></span>}
            {decision === 'APPROVE' ? 'Approve' : 'Reject'}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default CreditApprovalDetails;
